import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Button, Checkbox, Chip, Divider, InputAdornment, LinearProgress,
    Paper, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import FormModal from "../../../../component/common/FormModal";
import ResizableTable from "../../../../component/common/ResizableTable";
import TableToolbar from "../../../../component/common/TableToolbar";
import { useTableSearch } from "../../../../util/tableSearch";
import ActionButton from "../../../../component/common/ActionButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { today as getToday } from "../../../../util/dateUtils";
import { toNumericInputState, formatPercent, formatNumberWithCommas } from "../../../../util/numberFormat";
import FormField from "../../../../component/common/FormField";
import LoadingDots from "../../../../component/common/LoadingDots";
import ComboBox from "../../../../component/common/ComboBox";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import { useTableSort } from "../../../../util/useTableSort";

const MONTHS = ["연간", "1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];

export default function BudgetTab({ departments = [] }) {
    const now = getToday();
    const currentYear = now.year();
    const [year, setYear] = useState(currentYear);
    const [month, setMonth] = useState(now.month() + 1);
    const [rows, setRows] = useState([]);
    // 표 안 검색 — 공통 유틸이 행 전체를 훑는다.
    const [tableQuery, setTableQuery] = useState("");
    const rowsFiltered = useTableSearch(rows, tableQuery);

    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ deptId: "", year, month, budgetAmount: "", note: "" });
    const [budgetDisplay, setBudgetDisplay] = useState("");
    const [selected, setSelected] = useState(() => new Set()); // 일괄 삭제 선택

    const load = () => {
        setLoading(true);
        return financeApi.getBudgets(year, month)
            .then(setRows)
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [year, month]);

    // 공용 정렬 훅 사용(다른 재무 탭과 동일 UX). 로컬 재구현 제거.
    const { toggleSort, sortLabel, sortRows } = useTableSort("deptName", "asc");

    const totalBudget = rows.reduce((s, r) => s + (r.budgetAmount || 0), 0);
    const totalUsed = rows.reduce((s, r) => s + (r.usedAmount || 0), 0);

    const sortedRows = useMemo(() => sortRows(rowsFiltered), [rowsFiltered, sortRows]);

    const openCreate = () => {
        setForm({ deptId: "", year, month, budgetAmount: "", note: "" });
        setBudgetDisplay("");
        setOpen(true);
    };

    // 행 클릭 → 해당 팀 예산 수정 (saveBudget 이 upsert 라 그대로 저장하면 갱신됨)
    const openEdit = (row) => {
        setForm({
            deptId: row.deptId, year: row.year ?? year, month: row.month ?? month,
            budgetAmount: row.budgetAmount, note: row.note || "",
        });
        setBudgetDisplay(formatNumberWithCommas(row.budgetAmount, { emptyZero: true }));
        setOpen(true);
    };

    const handleBudgetInput = (raw) => {
        const { value, display } = toNumericInputState(raw, { emptyZero: true });
        setForm(f => ({ ...f, budgetAmount: value }));
        setBudgetDisplay(display);
    };

    const handleSave = async () => {
        await financeApi.saveBudget({ ...form, year: Number(form.year), month: Number(form.month), budgetAmount: Number(form.budgetAmount), deptId: Number(form.deptId) });
        setOpen(false);
        load();
    };

    // 좌측 체크박스 선택 → 일괄 삭제
    const toggleSelect = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const toggleSelectAll = (list) => {
        const targets = list.map(r => r.id);
        setSelected(prev => targets.length > 0 && targets.every(id => prev.has(id))
            ? new Set()
            : new Set(targets));
    };

    const handleBulkDelete = async () => {
        if (selected.size === 0) return;
        if (!(await confirmDialog({ message: `선택한 ${selected.size}건의 예산을 삭제하시겠습니까?` }))) return;
        await Promise.allSettled([...selected].map(id => financeApi.deleteBudget(id)));
        setSelected(new Set());
        load();
    };

    return (
        <Box>
            {/* 헤더 필터 */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center", flexWrap: "wrap" }}>
                <Box sx={{ width: 110 }}>
                    <ComboBox clearable={false}
                        options={[currentYear - 1, currentYear, currentYear + 1].map(y => ({ value: y, label: `${y}년` }))}
                        value={year}
                        onChange={v => setYear(Number(v))} />
                </Box>
                <Box sx={{ width: 100 }}>
                    <ComboBox clearable={false}
                        options={MONTHS.map((m, i) => ({ value: i, label: m }))}
                        value={month}
                        onChange={v => setMonth(Number(v))} />
                </Box>
                <Box sx={{ flex: 1 }} />
                {selected.size > 0 && (
                    <Box sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 2, py: 0.8, bgcolor: "#fef2f2", borderRadius: 1.5,
                    }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#dc2626" }}>
                            {selected.size}건 선택됨
                        </Typography>
                        {/* 검색 — 동작 버튼 왼쪽, 우측 상단 고정(전 시스템 공통 배치) */}
                        <TableToolbar onSearch={setTableQuery} placeholder="부서·항목 검색"
                            sx={{ flex: 1, mb: 0, minWidth: 200 }} />
                        <ActionButton variant="secondary" onClick={handleBulkDelete}
                            startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                            label="선택 삭제" sx={{ fontSize: 12, color: "#dc2626" }} />
                        <Chip label="선택 해제" size="small" onClick={() => setSelected(new Set())}
                            sx={{ height: 22, fontSize: 11, cursor: "pointer" }} />
                    </Box>
                )}
                <ActionButton variant="add" onClick={openCreate} label="예산 설정" />
            </Box>

            {/* 요약 카드 */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>총 예산</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#2563eb" }}>{fmtKRWFull(totalBudget)}</Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>사용 금액</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#dc2626" }}>{fmtKRWFull(totalUsed)}</Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>잔여 예산</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#16a34a" }}>{fmtKRWFull(totalBudget - totalUsed)}</Typography>
                </Paper>
            </Box>

            {/* 팀별 예산 테이블 */}
            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                    <Typography sx={ds.typography.sectionTitle}>
                        팀별 예산 현황 · {year}년 {month > 0 ? `${month}월` : "연간"}
                    </Typography>
                </Box>
                <Divider />
                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox size="small"
                                    checked={sortedRows.length > 0 && sortedRows.every(r => selected.has(r.id))}
                                    indeterminate={selected.size > 0 && !sortedRows.every(r => selected.has(r.id))}
                                    onChange={() => toggleSelectAll(sortedRows)} />
                            </TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "팀")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer" }} onClick={() => toggleSort("budgetAmount")}>{sortLabel("budgetAmount", "예산")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer" }} onClick={() => toggleSort("usedAmount")}>{sortLabel("usedAmount", "사용")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer" }} onClick={() => toggleSort("remainingAmount")}>{sortLabel("remainingAmount", "잔여")}</TableCell>
                            <TableCell sx={{ minWidth: 160 }}>사용률</TableCell>
                            <TableCell>비고</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#94a3b8" }}>설정된 예산이 없습니다.</TableCell>
                            </TableRow>
                        ) : sortedRows.map(row => {
                            const ratio = row.budgetAmount > 0 ? (row.usedAmount / row.budgetAmount) * 100 : 0;
                            return (
                                <TableRow key={row.id} hover onClick={() => openEdit(row)}
                                    sx={{ cursor: "pointer" }}>
                                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                        <Checkbox size="small" checked={selected.has(row.id)}
                                            onChange={() => toggleSelect(row.id)} />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>{row.deptName}</TableCell>
                                    <TableCell align="right">{fmtKRWFull(row.budgetAmount)}</TableCell>
                                    <TableCell align="right" sx={{ color: "#dc2626" }}>{fmtKRWFull(row.usedAmount)}</TableCell>
                                    <TableCell align="right" sx={{ color: row.remainingAmount >= 0 ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                                        {fmtKRWFull(row.remainingAmount)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(ratio, 100)}
                                                sx={{
                                                    flex: 1, height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
                                                    "& .MuiLinearProgress-bar": {
                                                        borderRadius: 3,
                                                        bgcolor: ratio > 90 ? "#dc2626" : ratio > 70 ? "#d97706" : "#2563eb",
                                                    },
                                                }}
                                            />
                                            <Typography sx={{ fontSize: 12, color: "#64748b", minWidth: 36 }}>{formatPercent(ratio, 0)}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12, color: "#64748b" }}>{row.note || "—"}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>}
            </Paper>

            {/* 예산 설정 다이얼로그 */}
            <FormModal
                open={open}
                onClose={() => setOpen(false)}
                title="예산 설정"
                maxWidth="xs"
                onSave={handleSave}
            >
                <Stack spacing={2}>
                    <FormField label="팀" required>
                        <ComboBox placeholder="팀 선택" clearable={false}
                            options={departments.map(d => ({ value: d.deptId, label: d.deptName }))}
                            value={form.deptId}
                            onChange={v => setForm(f => ({ ...f, deptId: v }))} />
                    </FormField>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <FormField label="연도" required sx={{ flex: 1 }}>
                            <ComboBox clearable={false}
                                options={[currentYear - 1, currentYear, currentYear + 1].map(y => ({ value: y, label: `${y}년` }))}
                                value={form.year}
                                onChange={v => setForm(f => ({ ...f, year: v }))} />
                        </FormField>
                        <FormField label="월" required sx={{ flex: 1 }}>
                            <ComboBox clearable={false}
                                options={MONTHS.map((m, i) => ({ value: i, label: m }))}
                                value={form.month}
                                onChange={v => setForm(f => ({ ...f, month: v }))} />
                        </FormField>
                    </Box>
                    <FormField label="예산 금액" required>
                        <TextField size="small" fullWidth
                            value={budgetDisplay}
                            onChange={e => handleBudgetInput(e.target.value)}
                            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                            inputProps={{ inputMode: "numeric" }} />
                    </FormField>
                    <FormField label="비고">
                        <TextField size="small" fullWidth value={form.note}
                            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                            placeholder="비고를 입력하세요" />
                    </FormField>
                </Stack>
            </FormModal>
        </Box>
    );
}
